<template>
  <section class="dvg">
    <header v-show="!sharedDataStore.isMobile()" class="dvg__head">
      <div class="dvg__head-text">
        <div class="dvg__eyebrow">
          <span class="dvg__dot"></span>
          <span class="brand-mark">WaizmannVergleich</span>&nbsp;/&nbsp;3-TARIFE-VERGLEICH&nbsp;·&nbsp;SEHHILFEN
        </div>
        <h2 class="dvg__title">Vergleichen Sie 3 Sehhilfen-Tarife</h2>
        <p class="dvg__sub">
          Wählen Sie zwei weitere Tarife, um Erstattungen, Wartezeiten und
          Zusatzleistungen direkt gegenüberzustellen.
        </p>
      </div>
    </header>

    <v-alert
      v-if="sharedDataStore.isMobile()"
      type="info"
      variant="tonal"
      density="comfortable"
      rounded="0"
      class="dvg__mobile"
      icon="mdi-phone-rotate-landscape"
    >
      Drehen Sie bitte Ihr Handy, um diesen Vergleich vollständig nutzen zu
      können.
    </v-alert>

    <div class="dvg__scroll">
      <table class="dvg__table">
        <thead>
          <tr>
            <th class="dvg__th dvg__th--label">Tarif</th>
            <th class="dvg__th dvg__th--tarif">
              <div class="dvg__tcol dvg__tcol--fix">
                <span class="dvg__tcol-eyebrow">Tarif&nbsp;1</span>
                <span class="dvg__tcol-name">
                  {{ tarif1.un_name }} {{ tarif1.tarif_title }}
                </span>
                <span class="dvg__tcol-price">
                  {{ tarif1.preis_value }}&nbsp;€
                  <span class="dvg__tcol-unit">mtl.</span>
                </span>
              </div>
            </th>
            <th class="dvg__th dvg__th--tarif">
              <div class="dvg__tcol">
                <span class="dvg__tcol-eyebrow">Tarif&nbsp;2</span>
                <select
                  class="dvg__select"
                  @change="pushTarifToVergleich($event, 2)"
                >
                  <option value selected>Tarif 2 wählen…</option>
                  <option
                    v-for="tarif2 in alletarifeFiltered"
                    :value="tarif2.tarif_id"
                    :key="tarif2.tarif_id"
                  >
                    {{ tarif2.un_name }} {{ tarif2.tarif_title }}
                  </option>
                </select>
                <span v-if="state.tarif2" class="dvg__tcol-price">
                  {{ state.tarif2.preis_value }}&nbsp;€
                  <span class="dvg__tcol-unit">mtl.</span>
                </span>
              </div>
            </th>
            <th class="dvg__th dvg__th--tarif">
              <div class="dvg__tcol">
                <span class="dvg__tcol-eyebrow">Tarif&nbsp;3</span>
                <select
                  class="dvg__select"
                  @change="pushTarifToVergleich($event, 3)"
                >
                  <option value selected>Tarif 3 wählen…</option>
                  <option
                    v-for="tarif3 in alletarifeFiltered"
                    :value="tarif3.tarif_id"
                    :key="tarif3.tarif_id"
                  >
                    {{ tarif3.un_name }} {{ tarif3.tarif_title }}
                  </option>
                </select>
                <span v-if="state.tarif3" class="dvg__tcol-price">
                  {{ state.tarif3.preis_value }}&nbsp;€
                  <span class="dvg__tcol-unit">mtl.</span>
                </span>
              </div>
            </th>
          </tr>
        </thead>

        <tbody>
          <tr>
            <td class="dvg__lab"><strong>Monatlicher Beitrag</strong></td>
            <td v-for="tarif in tarifs" :key="`pr-${tarif.tarif_id}`">
              <template v-if="tarif">
                <div class="dvg__price">{{ tarif.preis_value }}&nbsp;€</div>
                <div
                  v-if="tarif.empfehlung_type == 3"
                  class="dvg__badge dvg__badge--rec"
                >
                  Empfehlung „beste Leistung"
                </div>
                <div
                  v-if="
                    tarif.empfehlung_type == 1 || tarif.empfehlung_type == 2
                  "
                  class="dvg__badge"
                >
                  Alternativ-Empfehlung
                </div>
                <div v-if="tarif.zuschlag_sehhilfen > 0" class="dvg__hint">
                  inkl. {{ tarif.zuschlag_sehhilfen }} Zuschlag für Sehhilfen
                </div>
              </template>
            </td>
          </tr>

          <tr>
            <td class="dvg__lab">
              <strong>Beitragsentwicklung</strong>
              <div class="dvg__sublab">voraussichtlich</div>
            </td>
            <td v-for="tarif in tarifs" :key="`bew-${tarif.tarif_id}`">
              <template v-if="tarif">
                nächstes Jahr: <b>{{ tarif.preis_value_plus_1 }}&nbsp;€</b>
                <br />
                in 5 Jahren: <b>{{ tarif.preis_value_plus_5 }}&nbsp;€</b>
                <br />
                in 10 Jahren: <b>{{ tarif.preis_value_plus_10 }}&nbsp;€</b>
              </template>
            </td>
          </tr>

          <tr>
            <td class="dvg__lab">
              <strong>Infopaket</strong>
              <div class="dvg__sublab">kostenlos</div>
            </td>
            <td v-for="tarif in tarifs" :key="`btn-${tarif.tarif_id}`">
              <template v-if="tarif && tarif.antraglink">
                <v-btn
                  :href="tarif.antraglink"
                  variant="flat"
                  color="primary"
                  rounded="0"
                  density="comfortable"
                  size="default"
                  class="dvg__cta"
                  append-icon="mdi-arrow-right"
                >
                  Antrag &amp; Infopaket
                  <span
                    v-if="tarif.empfehlung_type > 0"
                    class="dvg__cta-tip"
                  >
                    Tipp
                  </span>
                </v-btn>
              </template>
            </td>
          </tr>

          <tr class="dvg__section">
            <td :colspan="showColspan">
              <span class="dvg__section-eyebrow">Erstattungen</span>
              <h3 class="dvg__section-title">
                Maximale Leistungen &amp; Begrenzungen
              </h3>
            </td>
          </tr>

          <tr>
            <td class="dvg__lab">
              <strong>Maximale Erstattung</strong>
              <div class="dvg__sublab">Details &amp; Zeiträume</div>
            </td>
            <td v-for="tarif in tarifs" :key="`me-${tarif.tarif_id}`">
              <strong>{{ tarif.bri_erstattung_max_eur }}&nbsp;€</strong>
              <span class="dvg__sublab">
                {{ tarif.bri_erstattung_max_zeit }}
              </span>
              <div v-if="tarif.bri_sehhilfe_detail" class="dvg__hint">
                {{ tarif.bri_sehhilfe_detail }}
              </div>
            </td>
          </tr>

          <tr>
            <td class="dvg__lab">
              <strong>Anfängliche Begrenzungen</strong>
              <div class="dvg__sublab">erste Jahre</div>
            </td>
            <td v-for="tarif in tarifs" :key="`be-${tarif.tarif_id}`">
              {{ tarif.bri_begrenzung_zusatz_erste_jahre }}
            </td>
          </tr>

          <tr>
            <td class="dvg__lab">
              <strong>Wartezeiten</strong>
              <div class="dvg__sublab">Vertragsbedingungen</div>
            </td>
            <td v-for="tarif in tarifs" :key="`wz-${tarif.tarif_id}`">
              <span v-if="tarif.bri_wartezeit">{{ tarif.bri_wartezeit }}</span>
              <strong v-else class="dvg__pos">Keine Wartezeit</strong>
            </td>
          </tr>

          <tr class="dvg__section">
            <td :colspan="showColspan">
              <span class="dvg__section-eyebrow">Brillen &amp; Linsen</span>
              <h3 class="dvg__section-title">Erstattung für Sehhilfen</h3>
            </td>
          </tr>

          <tr>
            <td class="dvg__lab"><strong>Sonnenbrillen</strong></td>
            <td v-for="tarif in tarifs" :key="`sb-${tarif.tarif_id}`">
              {{ tarif.bri_erstattung_sonnenbrille_short }}
            </td>
          </tr>

          <tr>
            <td class="dvg__lab"><strong>Gleitsichtbrille</strong></td>
            <td v-for="tarif in tarifs" :key="`gl-${tarif.tarif_id}`">
              <strong v-if="tarif.bri_gleitsicht == 1" class="dvg__pos">
                Ja
              </strong>
              <span v-else class="dvg__neg">Nein</span>
            </td>
          </tr>

          <tr>
            <td class="dvg__lab"><strong>Kontaktlinsen</strong></td>
            <td v-for="tarif in tarifs" :key="`kl-${tarif.tarif_id}`">
              <strong v-if="tarif.bri_erstattung_linse == 1" class="dvg__pos">
                Ja
              </strong>
              <span v-else class="dvg__neg">Nein</span>
            </td>
          </tr>

          <tr>
            <td class="dvg__lab"><strong>Prismenbrille</strong></td>
            <td v-for="tarif in tarifs" :key="`pr2-${tarif.tarif_id}`">
              {{ tarif.bri_erstattung_prisma_short }}
            </td>
          </tr>

          <tr class="dvg__section">
            <td :colspan="showColspan">
              <span class="dvg__section-eyebrow">Zusatzleistungen</span>
              <h3 class="dvg__section-title">Vorsorge, Lasik &amp; mehr</h3>
            </td>
          </tr>

          <tr>
            <td class="dvg__lab"><strong>Glaukom-Vorsorge</strong></td>
            <td v-for="tarif in tarifs" :key="`gv-${tarif.tarif_id}`">
              {{ tarif.bri_glaukom }}
            </td>
          </tr>

          <tr>
            <td class="dvg__lab"><strong>Lasik</strong></td>
            <td v-for="tarif in tarifs" :key="`la-${tarif.tarif_id}`">
              <strong v-if="tarif.bri_lasik_leistung == 1" class="dvg__pos">
                {{ tarif.bri_lasik_begrenzung }}
              </strong>
              <span v-else class="dvg__neg">Nein</span>
            </td>
          </tr>

          <tr>
            <td class="dvg__lab"><strong>Vorsorge-Untersuchungen</strong></td>
            <td v-for="tarif in tarifs" :key="`vs-${tarif.tarif_id}`">
              {{ tarif.bri_zusatz_vorsorge_short }}
            </td>
          </tr>

          <tr>
            <td class="dvg__lab"><strong>Zusatzimpfungen</strong></td>
            <td v-for="tarif in tarifs" :key="`zi-${tarif.tarif_id}`">
              {{ tarif.bri_zusatz_impfung_short }}
            </td>
          </tr>

          <tr>
            <td class="dvg__lab"><strong>Hörgeräte</strong></td>
            <td v-for="tarif in tarifs" :key="`hg-${tarif.tarif_id}`">
              {{ tarif.bri_zusatz_hoergeraet_zahl_short }}
              <div v-if="tarif.bri_zusatz_hoergeraet_detail" class="dvg__hint">
                {{ tarif.bri_zusatz_hoergeraet_detail }}
              </div>
            </td>
          </tr>

          <tr>
            <td class="dvg__lab">
              <strong>Weitere Leistungen</strong>
            </td>
            <td v-for="tarif in tarifs" :key="`zl-${tarif.tarif_id}`">
              <ul v-if="tarif.bri_umfang && tarif.bri_umfang.length" class="dvg__list">
                <li v-for="item in tarif.bri_umfang" :key="item.id">
                  <span
                    :class="item.no_value ? 'dvg__list-no' : 'dvg__list-ok'"
                    v-html="item.leistung"
                  ></span>
                  <template v-if="item.details">
                    …
                    <a class="dvg__more" @click="item.show_detail = !item.show_detail">
                      mehr
                    </a>
                    <div v-if="item.show_detail" class="dvg__hint">
                      {{ item.details }}
                    </div>
                  </template>
                </li>
              </ul>
            </td>
          </tr>

          <tr class="dvg__section">
            <td :colspan="showColspan">
              <span class="dvg__section-eyebrow">Konditionen</span>
              <h3 class="dvg__section-title">
                Vertragslaufzeit &amp; Kündigung
              </h3>
            </td>
          </tr>

          <tr>
            <td class="dvg__lab"><strong>Mindestvertragslaufzeit</strong></td>
            <td v-for="tarif in tarifs" :key="`mv-${tarif.tarif_id}`">
              {{ tarif.bri_vertrag_Mindestvertragszeit }}
            </td>
          </tr>

          <tr>
            <td class="dvg__lab"><strong>Kündigungsfrist</strong></td>
            <td v-for="tarif in tarifs" :key="`ku-${tarif.tarif_id}`">
              {{ tarif.tarif_int_kuendigung_details }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<script setup>
  import { ref, onMounted, watch, reactive, computed, defineProps } from "vue";
  import { useSharedDataStore } from "@/stores/sharedData";
  const sharedDataStore = useSharedDataStore();
  const props = defineProps(["tarif1", "alletarife", "berechnung"]);

  const alletarifeFiltered = ref([]);
  const showColspan = ref(2);

  const state = reactive({
    tarif2: null,
    tarif3: null,
  });

  const tarifs = computed(() => {
    let arr = [props.tarif1];
    if (state.tarif2 !== null) arr.push(state.tarif2);
    if (state.tarif3 !== null) arr.push(state.tarif3);
    return arr;
  });

  onMounted(() => {
    filterAlle();
  });

  function filterAlle() {
    alletarifeFiltered.value = props.alletarife.filter(
      (x) => x.tarif_id !== parseInt(props.tarif1.tarif_id)
    );
  }

  function pushTarifToVergleich(event, tarif_nr) {
    const treffer = props.alletarife.filter(
      (x) => parseInt(x.tarif_id) === parseInt(event.target.value)
    );
    if (tarif_nr === 2) state.tarif2 = treffer[0] || null;
    if (tarif_nr === 3) state.tarif3 = treffer[0] || null;
  }

  watch(
    () => tarifs.value.length,
    (newLength) => {
      showColspan.value = newLength + 1;
    },
    { immediate: true }
  );
</script>

<style scoped>
  .dvg {
    padding: 12px 18px 24px;
    max-width: 1280px;
    margin: 0 auto;
  }

  .dvg__head {
    background: #ffffff;
    border: 1px solid #d8dde5;
    border-left: 3px solid #0f4c81;
    border-radius: 3px;
    padding: 12px 16px;
    margin-bottom: 14px;
  }
  .dvg__eyebrow {
    display: inline-flex;
    align-items: center;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1.2px;
    color: #5b6473;
    text-transform: uppercase;
    margin-bottom: 4px;
  }
  .dvg__dot {
    display: inline-block;
    width: 6px;
    height: 6px;
    background: #0f4c81;
    margin-right: 8px;
  }
  .dvg__title {
    margin: 0;
    font-size: clamp(18px, 2.2vw, 22px);
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.2px;
    line-height: 1.2;
  }
  .dvg__sub {
    margin: 6px 0 0;
    font-size: 13px;
    color: #4b5563;
    line-height: 1.5;
  }

  .dvg__mobile {
    margin-bottom: 14px;
  }

  .dvg__scroll {
    width: 100%;
    overflow-x: auto;
    border: 1px solid #e5e9f0;
    border-radius: 3px;
    background: #ffffff;
  }

  .dvg__table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    color: #1f2937;
    min-width: 720px;
  }
  .dvg__table th,
  .dvg__table td {
    padding: 10px 12px;
    vertical-align: top;
    border-bottom: 1px solid #eef1f6;
    text-align: left;
  }
  .dvg__table tbody tr:nth-child(even) td {
    background: #fbfcfe;
  }

  .dvg__th {
    background: #f5f7fb;
    border-bottom: 2px solid #0f4c81;
    position: sticky;
    top: 0;
    z-index: 1;
  }
  .dvg__th--label {
    width: 220px;
    font-weight: 700;
    color: #0f4c81;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    font-size: 11.5px;
  }
  .dvg__th--tarif {
    width: 33%;
    min-width: 220px;
  }

  .dvg__tcol {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .dvg__tcol-eyebrow {
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 1.1px;
    color: #5b6473;
    text-transform: uppercase;
  }
  .dvg__tcol-name {
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.25;
  }
  .dvg__tcol-price {
    font-size: 16px;
    font-weight: 800;
    color: #0f4c81;
    letter-spacing: -0.3px;
    font-variant-numeric: tabular-nums;
  }
  .dvg__tcol-unit {
    font-size: 11px;
    color: #6b7280;
    font-weight: 600;
    margin-left: 2px;
  }
  .dvg__tcol--fix {
    border-bottom: 0;
  }

  .dvg__select {
    width: 100%;
    padding: 6px 8px;
    font-size: 13px;
    border: 1px solid #c8d0db;
    border-radius: 3px;
    background: #fff;
    color: #0f172a;
    font-weight: 600;
    outline: none;
  }
  .dvg__select:focus {
    border-color: #0f4c81;
    box-shadow: 0 0 0 2px rgba(15, 76, 129, 0.12);
  }

  .dvg__lab {
    width: 220px;
    background: #ffffff;
    border-right: 1px solid #eef1f6;
  }
  .dvg__lab strong {
    color: #0f172a;
    font-weight: 700;
  }
  .dvg__sublab {
    font-size: 11.5px;
    color: #6b7280;
    margin-top: 2px;
  }

  .dvg__price {
    font-size: 15px;
    font-weight: 800;
    color: #0f172a;
    letter-spacing: -0.2px;
    font-variant-numeric: tabular-nums;
  }

  .dvg__badge {
    display: inline-block;
    margin-top: 4px;
    padding: 2px 8px;
    border-radius: 3px;
    background: #f0f5fa;
    border: 1px solid #d8e3ee;
    color: #0f4c81;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.4px;
    text-transform: uppercase;
  }
  .dvg__badge--rec {
    background: #fff5cc;
    border-color: #ffe58f;
    color: #8a6d00;
  }

  .dvg__hint {
    font-size: 11.5px;
    color: #6b7280;
    margin-top: 3px;
  }

  .dvg__cta {
    font-weight: 700;
    letter-spacing: 0.3px;
    text-transform: none;
    color: #fff !important;
  }
  .dvg__cta-tip {
    margin-left: 6px;
    padding: 1px 6px;
    background: rgba(255, 255, 255, 0.18);
    border: 1px solid rgba(255, 255, 255, 0.35);
    border-radius: 3px;
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.6px;
    text-transform: uppercase;
  }

  .dvg__pos {
    color: #2e7d32;
  }
  .dvg__neg {
    color: #b91c1c;
  }

  .dvg__section td {
    background: #f7faff !important;
    border-top: 2px solid #0f4c81;
    border-bottom: 1px solid #d8e3ee;
    padding: 10px 12px !important;
  }
  .dvg__section-eyebrow {
    display: inline-block;
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 1.1px;
    color: #0f4c81;
    text-transform: uppercase;
    margin-bottom: 2px;
  }
  .dvg__section-title {
    margin: 0;
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.1px;
  }

  .dvg__list {
    list-style: disc outside;
    margin: 0;
    padding-left: 18px;
  }
  .dvg__list li::marker {
    color: #0f4c81;
  }
  .dvg__list li {
    margin: 1px 0;
    font-size: 12.5px;
  }
  .dvg__list-no {
    color: #b91c1c;
    text-decoration: line-through;
  }
  .dvg__list-ok {
    color: #1f2937;
  }
  .dvg__more {
    color: #0f4c81;
    font-weight: 600;
    cursor: pointer;
  }

  @media (max-width: 720px) {
    .dvg {
      padding: 8px 10px 18px;
    }
    .dvg__th--label,
    .dvg__lab {
      width: 160px;
      min-width: 160px;
    }
  }
</style>
